import { PriceUtils } from '@wix/bookings-uou-mappers';
import { useSettings } from '@wix/tpa-settings/react';
import { TFunction, useTranslation } from '@wix/yoshi-flow-editor';
import React from 'react';
import { Divider, Spinner } from 'wix-ui-tpa/cssVars';
import { PaymentDetails as PaymentDetailsType } from '../../../../types/dynamicPrice';
import { FormStatus } from '../../../../types/form-state';
import { ReservedPaymentOptionIds } from '../../../../types/types';
import { getServiceSlotIdentifier, mapToArray } from '../../../../utils';
import { getContent } from '../../../../utils/content/content';
import { DepositOrFullAmountOptionIds } from '../../../../utils/payment/paymentOptions/utils';
import { ServiceData, SlotService } from '../../../../utils/state/types';
import { TagName } from '../../../../utils/tagName.const';
import settingsParams from '../../settingsParams';
import Text, { TextType } from '../../Text/Text';
import { PaymentDetails } from '../PaymentDetails/PaymentDetails';
import { PaymentSummaryDataHook } from './dataHooks';
import { classes, st } from './PaymentSummary.st.css';
import { PriceItem } from './PriceItem/PriceItem';

import { getMaxNumberOfParticipants } from '../../../../utils/numberOfParticipant';
import { useFormComponentContext } from '../../Hooks/useFormComponentContext';

export type PaymentSummaryProps = {
  serviceData: ServiceData;
  numberOfParticipants: number;
  status: FormStatus;
  isCart: boolean | undefined;
};

export const PaymentSummary: React.FC<PaymentSummaryProps> = ({
  serviceData,
  numberOfParticipants,
  status,
  isCart,
}) => {
  const { dateRegionalSettingsLocale } = useFormComponentContext();

  const {
    paymentDetails,
    dynamicPriceInfo,
    selectedPaymentOption,
    nestedSlot,
  } = mapToArray<SlotService>(serviceData.slotServices)[0];

  const maxNumberOfParticipants = getMaxNumberOfParticipants(
    serviceData,
    getServiceSlotIdentifier(nestedSlot),
  );

  const showPricePerParticipant =
    maxNumberOfParticipants > 1 && !dynamicPriceInfo;

  const { t } = useTranslation();
  const settings = useSettings();

  const sectionTitle = getContent({
    settings,
    settingsParam: settingsParams.summaryPaymentSectionTitle,
  });

  const isProcessingPaymentDetails =
    status === FormStatus.PROCESSING_PAYMENT_DETAILS;

  const { tax, subtotalPerParticipant, payNow, payLater, totalPrice } =
    serviceData.summaryPaymentDetails;
  const {
    price,
    priceText,
    currency,
    priceBeforeDiscount,
    discountRuleName,
    minCharge: deposit,
  } = paymentDetails;

  const isDepositOrFullAmountOption =
    selectedPaymentOption?.id === DepositOrFullAmountOptionIds.Deposit ||
    selectedPaymentOption?.id === DepositOrFullAmountOptionIds.FullAmount;

  const isPricingPlanSelected =
    !selectedPaymentOption?.disabled &&
    ![
      ReservedPaymentOptionIds.SingleSession,
      ReservedPaymentOptionIds.BuyAPricingPlan,
    ].includes(selectedPaymentOption?.id as ReservedPaymentOptionIds) &&
    !isDepositOrFullAmountOption;

  const serviceHasPrice = price > 0 || !!priceText || priceBeforeDiscount;
  const showSingleSessionSummary =
    (selectedPaymentOption?.id === ReservedPaymentOptionIds.SingleSession ||
      isDepositOrFullAmountOption) &&
    serviceHasPrice;

  const shouldShowPaymentSummary =
    showSingleSessionSummary || isPricingPlanSelected;

  let subtotalItems: PaymentDetailsType[] = [];
  if (dynamicPriceInfo) {
    const defaultPrice = dynamicPriceInfo.defaultPrice;
    subtotalItems = (dynamicPriceInfo.paymentDetails || []).map(
      ({ price: choicePrice, ...rest }) => ({
        ...rest,
        price: choicePrice || {
          currency: defaultPrice!.currency,
          value: defaultPrice!.amount,
        },
      }),
    );
  }

  const priceBeforeDiscountText = priceBeforeDiscount
    ? PriceUtils.getFormattedCurrency({
        price: priceBeforeDiscount,
        currency,
        locale: dateRegionalSettingsLocale,
      })
    : undefined;

  const priceBreakDownItems = getPriceBreakDownItems({
    locale: dateRegionalSettingsLocale,
    numberOfParticipants,
    showPricePerParticipant,
    tax,
    subtotalPerParticipant,
    currency,
    t,
    subtotalItems,
    priceBeforeDiscountText,
  });

  const totalPriceItems = getTotalPriceItems({
    locale: dateRegionalSettingsLocale,
    price,
    totalPrice,
    payNow,
    payLater,
    deposit,
    t,
    currency,
    priceBeforeDiscount,
  });

  return shouldShowPaymentSummary ? (
    <div
      className={st(classes.root, { isProcessingPaymentDetails })}
      data-hook={PaymentSummaryDataHook.Payment_Summary}
    >
      <Text
        tagName={TagName.H3}
        type={TextType.Primary}
        className={classes.title}
        data-hook={PaymentSummaryDataHook.Title}
      >
        {sectionTitle}
      </Text>
      {showSingleSessionSummary ? (
        <div className={classes.singleSession}>
          {isProcessingPaymentDetails ? (
            <div className={classes.spinner}>
              <Spinner
                isCentered
                diameter={24}
                data-hook={PaymentSummaryDataHook.Spinner}
              />
            </div>
          ) : null}
          <div className={classes.details}>
            {priceText ? (
              <div>
                <Text
                  type={TextType.Secondary}
                  data-hook={PaymentSummaryDataHook.Custom_Price}
                >
                  {priceText}
                </Text>
              </div>
            ) : (
              <>
                {priceBreakDownItems.map((item, index) => (
                  <PriceItem
                    key={index}
                    type={TextType.Secondary}
                    labels={item.labels}
                    price={item.price}
                    ariaPrice={item.ariaPrice}
                    dataHook={item.dataHook}
                    priceBeforeDiscountText={item.priceBeforeDiscountText}
                    isCart={isCart}
                  />
                ))}
                {priceBreakDownItems.length ? (
                  <Divider className={classes.divider} />
                ) : null}
                {totalPriceItems.map((totalItem, index) => (
                  <PriceItem
                    key={index}
                    type={TextType.Primary}
                    labels={totalItem.labels}
                    price={totalItem.price}
                    ariaPrice={totalItem.ariaPrice}
                    discountRuleName={
                      totalItem.isTotalPrice ? discountRuleName : undefined
                    }
                    priceBeforeDiscountText={
                      totalItem.isTotalPrice
                        ? priceBeforeDiscountText
                        : undefined
                    }
                    dataHook={totalItem.dataHook}
                    isCart={isCart}
                  />
                ))}
              </>
            )}
          </div>
        </div>
      ) : (
        <PaymentDetails
          paymentOption={selectedPaymentOption}
          payNowAmount={payNow}
          payLaterAmount={payLater}
          currency={currency}
          discountRuleName={discountRuleName}
          priceBeforeDiscountText={priceBeforeDiscountText}
          isCart={isCart}
        />
      )}
      <Divider className={classes.divider} />
    </div>
  ) : null;
};

const getPriceBreakDownItems = ({
  numberOfParticipants,
  showPricePerParticipant,
  tax,
  subtotalPerParticipant,
  currency,
  locale,
  t,
  subtotalItems,
  priceBeforeDiscountText,
}: {
  numberOfParticipants: number;
  showPricePerParticipant: boolean;
  tax?: number;
  subtotalPerParticipant?: number;
  currency: string;
  locale: string;
  t: TFunction;
  subtotalItems: PaymentDetailsType[];
  priceBeforeDiscountText: string | undefined;
}) => {
  const isSubtotalNeeded =
    showPricePerParticipant || tax || subtotalItems.length > 0;
  const shouldShowSubtotal = subtotalPerParticipant && isSubtotalNeeded;

  return [
    ...(shouldShowSubtotal
      ? [
          {
            labels: [
              t('app.payment.summary.subtotal.text'),
              ...(showPricePerParticipant && !subtotalItems.length
                ? [
                    t('app.payment.summary.number-of-participants.text', {
                      numberOfParticipants,
                      price: PriceUtils.getFormattedCurrency({
                        price: subtotalPerParticipant,
                        currency,
                        locale,
                      }),
                    }),
                  ]
                : []),
              ...subtotalItems.map(
                ({
                  price: choicePrice,
                  numberOfParticipants: choiceNumberOfParticipants,
                  choice,
                }) =>
                  t('app.payment.summary.number-of-custom-choice.text', {
                    numberOfParticipants: choiceNumberOfParticipants,
                    choice,
                    price: PriceUtils.getFormattedCurrency({
                      price: Number(choicePrice!.value),
                      currency: choicePrice!.currency!,
                      locale,
                    }),
                  }),
              ),
            ],
            price: PriceUtils.getFormattedCurrency({
              price: subtotalPerParticipant,
              currency,
              locale,
              quantity: numberOfParticipants,
            }),
            ariaPrice: PriceUtils.getFormattedCurrency({
              price: subtotalPerParticipant,
              currency,
              locale,
              quantity: numberOfParticipants,
              currencyDisplay: 'name',
            }),
            dataHook: PaymentSummaryDataHook.Subtotal,
            priceBeforeDiscountText,
          },
        ]
      : []),
    ...(tax
      ? [
          {
            labels: [t('app.payment.summary.tax.text')],
            price: PriceUtils.getFormattedCurrency({
              price: tax,
              currency,
              locale,
            }),
            ariaPrice: PriceUtils.getFormattedCurrency({
              price: tax,
              currency,
              locale,
              currencyDisplay: 'name',
            }),
            dataHook: PaymentSummaryDataHook.Tax,
          },
        ]
      : []),
  ];
};

const getTotalPriceItems = ({
  price,
  totalPrice,
  payNow,
  payLater,
  currency,
  locale,
  t,
  priceBeforeDiscount,
}: {
  price: number;
  totalPrice: number;
  payNow: number | undefined;
  payLater: number | undefined;
  deposit: number;
  currency: string;
  locale: string;
  t: TFunction;
  priceBeforeDiscount?: number;
}) => {
  const payNowAmount = payLater ? payNow : 0;
  const shouldShowPayLaterSection = payLater && payLater !== totalPrice;
  const shouldShowTotalPrice = price || priceBeforeDiscount;

  return [
    ...(shouldShowTotalPrice
      ? [
          {
            labels: [t('app.payment.summary.total.text')],
            price: PriceUtils.getFormattedCurrency({
              price: totalPrice,
              currency,
              locale,
            }),
            ariaPrice: PriceUtils.getFormattedCurrency({
              price: totalPrice,
              currency,
              locale,
              currencyDisplay: 'name',
            }),
            dataHook: PaymentSummaryDataHook.Total_Price,
            isTotalPrice: true,
          },
        ]
      : []),
    ...(payNowAmount
      ? [
          {
            labels: [t('app.payment.summary.pay-now.text')],
            price: PriceUtils.getFormattedCurrency({
              price: payNowAmount,
              currency,
              locale,
            }),
            ariaPrice: PriceUtils.getFormattedCurrency({
              price: payNowAmount,
              currency,
              locale,
              currencyDisplay: 'name',
            }),
            dataHook: PaymentSummaryDataHook.Deposit,
          },
        ]
      : []),
    ...(shouldShowPayLaterSection
      ? [
          {
            labels: [t('app.payment.summary.pay-later.text')],
            price: PriceUtils.getFormattedCurrency({
              price: payLater,
              currency,
              locale,
            }),
            ariaPrice: PriceUtils.getFormattedCurrency({
              price: payLater,
              currency,
              locale,
              currencyDisplay: 'name',
            }),
            dataHook: PaymentSummaryDataHook.Pay_Later,
          },
        ]
      : []),
  ];
};
